import {
	Currency,
	DefaultErrorMessage,
	DefaultTimestampFormat,
	NotificationConfiguration,
	PurchaseOrderAddress,
	TableQueryFilter,
	TableTimestampFilterKeys,
	ZomentumRoutes,
	ZomentumStorageKeys,
	ZomentumWindow
} from "../common";
import { RGBColor } from "react-color";
import isNull from "lodash/isNull";
import moment from "moment";

import { htmlToText } from "html-to-text";
import {
	ConfigurationSettingsResponse,
	PricingAddOn,
	PricingPlanTiers,
	UserCompany
} from "../Users";
import { UserType } from "../Documents";
import { ProductSource } from "../Products";
import { WrappedFormUtils } from "@ant-design/compatible/lib/form/Form";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import xorWith from "lodash/xorWith";
import { CustomField, CustomFieldType, KaseyaErrorObject } from "../Settings";
import { AuthToken } from "../Login";
import cache, { cacheConstant } from "../appCache";
import { EIcons, ETwoToneIcons } from "@atoms/ZIcon/interface";
import { isValidElement, ReactElement } from "react";
import { ResourceFileType } from "../common";
import axios, { AxiosRequestConfig } from "axios";

declare let window: ZomentumWindow;

export const getServerErrorMessage = (error: any): string => {
	const responseData = error?.response?.data ?? error?.data;
	const description = responseData?.description;
	if (typeof description === "string" && description.length > 0)
		return description;
	const message = responseData?.message;
	if (typeof message === "string" && message.length > 0) return message;
	return DefaultErrorMessage;
};

export const formatRupeeCurrency = (amount: number | string): string => {
	let result = "0";
	if (amount) {
		amount = amount.toString();
		amount = amount.split(",").join("");
		amount = Number(amount);
		if (!isNaN(amount)) {
			amount = Number(amount);
			amount = amount.toFixed(2);
			const decimal = amount.split(".");
			const beforeDecimal = decimal[0];
			const afterDecimal = decimal[1];
			amount = beforeDecimal;
			let lastThree = amount.substring(amount.length - 3);
			const otherNumbers = amount.substring(0, amount.length - 3);
			if (otherNumbers !== "") {
				lastThree = `,${lastThree}`;
			}
			result =
				otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
			if (afterDecimal && afterDecimal !== "00") {
				result = `${result}.${afterDecimal}`;
			}
		}
	}
	return "&#x20b9; " + result;
};

export const delay = async (delayTimeInMilliseconds: number) => {
	return new Promise<void>(resolve => {
		setTimeout(() => {
			resolve();
		}, delayTimeInMilliseconds);
	});
};

export const removeEscapeSlashes = (
	stringWithEscapedCharacters?: string
): string | undefined => {
	return stringWithEscapedCharacters?.replace(/\\"/g, "")?.replace(/"/g, "");
};

export const capitalizeFirstLetter = (
	input: string | undefined | null
): string => {
	if (!input || input.length === 0) {
		return "";
	}
	return input.charAt(0).toUpperCase() + input.slice(1);
};

export const capitalizeAfterComma = (
	input: string | undefined | null
): string => {
	if (!input || input.length === 0) {
		return "";
	}
	return input
		.split(",")
		.join(", ")
		.replace(/\b\w/g, (word: string) => word.toUpperCase());
};

export const splitAfterUnderscore = (
	input: string | undefined | null
): string => {
	if (!input || input.length === 0) {
		return "";
	}
	return input.split("_").join(" ");
};

export const splitAndCapitalizeAfterUnderScore = (
	input: string | undefined | null
): string => {
	if (!input?.length) {
		return "";
	}
	return input
		.split("_")
		.join(" ")
		.replace(/\b\w/g, (word: string) => word.toUpperCase());
};

export const allZeroesGuid: string = "0".repeat(32);
export const allZeroesPartnerOwner: string = `${allZeroesGuid}_partner_owner`;

export const createGuid = (): string => {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v?.toString(16)?.replace(/-/g, "");
	});
};

export const convertFileSizeFormat = (fileSizeInBytes: number) => {
	if (!fileSizeInBytes || isNaN(fileSizeInBytes) || fileSizeInBytes <= 0) {
		return "0 KB";
	}
	if (fileSizeInBytes <= 1024) {
		return "0.1 KB";
	} else if (fileSizeInBytes <= Math.pow(1024, 2)) {
		return Math.floor(fileSizeInBytes / 1024) + " KB";
	} else if (fileSizeInBytes <= Math.pow(1024, 3)) {
		return Math.floor(fileSizeInBytes / Math.pow(1024, 2)) + " MB";
	} else if (fileSizeInBytes <= Math.pow(1024, 4)) {
		return Math.floor(fileSizeInBytes / Math.pow(1024, 3)) + " GB";
	} else {
		return Math.floor(fileSizeInBytes / Math.pow(1024, 4)) + " TB";
	}
};

export const getAvatarBackgroundColor = (avatar = ""): string => {
	if (avatar === null || avatar === undefined) {
		avatar = "";
	}
	if (avatar.length === 0) {
		return "#CCCCCC";
	}
	let sum = 0;
	const avatarBackgroundColors = [
		"#DAC4E1",
		"#C3D6BA",
		"#B5D8E0",
		"#DCEAB7",
		"#D3A8A8",
		"#E9CEBF",
		"#ACCAC1"
	];
	for (let i = 0; i < avatar.length; i++) {
		sum += avatar.charCodeAt(i);
	}
	return avatarBackgroundColors[sum % avatarBackgroundColors.length];
};

export const getAvatarInitials = (name = ""): string => {
	return name
		? name
				.split(" ")
				.map(word => word[0] + "")
				.join(" ")
				.substring(0, 3)
				.toUpperCase()
		: "--";
};

export const getCustomDateRangeFilterValue = (
	tableTimestampFilter: string,
	startDate?: moment.Moment,
	endDate?: moment.Moment
): string => {
	let dateRangeString = "";
	if (tableTimestampFilter && tableTimestampFilter.length > 0) {
		switch (tableTimestampFilter) {
			case TableTimestampFilterKeys.Last24Hours: {
				dateRangeString = `${moment()
					.subtract(1, "day")
					.valueOf()},${moment().valueOf()}`;
				break;
			}
			case TableTimestampFilterKeys.Yesterday: {
				dateRangeString = `${moment()
					.subtract(1, "day")
					.startOf("day")
					.valueOf()},${moment().startOf("day").valueOf()}`;
				break;
			}
			case TableTimestampFilterKeys.Next24Hours: {
				dateRangeString = `${moment().valueOf()},${moment()
					.add(1, "day")
					.valueOf()}`;
				break;
			}
			case TableTimestampFilterKeys.Last7Days: {
				dateRangeString = `${moment()
					.subtract(7, "day")
					.valueOf()},${moment().valueOf()}`;
				break;
			}
			case TableTimestampFilterKeys.Next7Days: {
				dateRangeString = `${moment().valueOf()},${moment()
					.add(7, "day")
					.valueOf()}`;
				break;
			}
			case TableTimestampFilterKeys.Last30Days: {
				dateRangeString = `${moment()
					.subtract(30, "day")
					.valueOf()},${moment().valueOf()}`;
				break;
			}
			case TableTimestampFilterKeys.Next30Days: {
				dateRangeString = `${moment().valueOf()},${moment()
					.add(30, "day")
					.valueOf()}`;
				break;
			}
			case TableTimestampFilterKeys.Custom: {
				if (startDate && endDate) {
					dateRangeString = `${moment(startDate)
						.startOf("day")
						.valueOf()},${moment(endDate).endOf("day").valueOf()}`;
				}
				break;
			}
			case TableTimestampFilterKeys.Today: {
				dateRangeString = `${moment()
					.startOf("day")
					.valueOf()},${moment().endOf("day").valueOf()}`;
				break;
			}
			case TableTimestampFilterKeys.ThisWeek: {
				dateRangeString = `${moment()
					.startOf("isoWeek")
					.valueOf()},${moment().endOf("isoWeek").valueOf()}`;
				break;
			}
			default: {
				break;
			}
		}
	}
	return dateRangeString;
};

export const getTableQueryFilters = (
	tableQueryFilters: TableQueryFilter[],
	urlEncode: boolean
): string => {
	let filter = "filters=";
	tableQueryFilters =
		tableQueryFilters && tableQueryFilters.length > 0
			? tableQueryFilters
			: new Array<TableQueryFilter>();
	const searchFilters = tableQueryFilters.filter(
		tableQueryFilter =>
			tableQueryFilter.filterEnable === true &&
			tableQueryFilter.filterKey !== "sort" &&
			tableQueryFilter.filterKey !== "page" &&
			tableQueryFilter.filterKey !== "limit"
	);

	if (searchFilters && searchFilters.length > 0) {
		let filters = "";
		searchFilters.forEach(searchFilter => {
			filters =
				filters +
				searchFilter.filterKey +
				searchFilter.operand +
				searchFilter.filterValue +
				";";
		});
		filter = filter + filters;
	}
	const pageFilters = tableQueryFilters.filter(
		tableQueryFilter =>
			(tableQueryFilter.filterEnable === true &&
				tableQueryFilter.filterKey === "sort") ||
			tableQueryFilter.filterKey === "page" ||
			tableQueryFilter.filterKey === "limit"
	);
	if (pageFilters && pageFilters.length > 0) {
		pageFilters.forEach(pageFilter => {
			filter =
				filter +
				"&" +
				pageFilter.filterKey +
				pageFilter.operand +
				pageFilter.filterValue;
		});
	}
	return urlEncode ? encodeURIComponent(filter) : filter;
};

export enum ChildEntityHydrationParams {
	BLOCKS_CURRENT_REVISION = "blocks:current_revision",
	CLIENT_USERS_ALL = "client_users:all",
	CLIENT_USERS_COUNT = "client_users:count",
	COMMENTS_ALL = "comments:all",
	DOCUMENTS_ALL = "documents:all",
	OPPORTUNITIES_ALL = "opportunities:all",
	OPPORTUNITY_COUNT = "opportunities:count",
	OPPORTUNITY_NAME = "opportunities:name",
	USERS_ALL = "users:all",
	COMPANIES_ALL = "companies:all",
	CLIENT_COMPANIES_NAME = "client_companies:name",
	OPPORTUNITY_ACTION_STATUS = "sales_activities:action_status",
	ASSESSMENT_LOGS = "assessment_logs:all",
	ASSESSMENT_BLOCKS = "assessment_logs:blocks",
	QBR_BLOCKS_ALL = "qbr_logs:blocks",
	QBR_RISK_MATRIX_BLOCKS = "qbr_risk_matrix:blocks",
	QBR_HEALTH_STATUS_BLOCK = "qbr_health_status:block",
	TAX = "tax:all",
	PARENT_ENTITY_SUMMARY = "parent_entity:summary",
	APPROVAL_AUTOMATION_RULES = "approval_automation_rules:all",
	CLIENT_COMPANY_SYNC_DATA = "client_details:sync_data",
	VENDOR_PARTNER_COUNT = "company:count",
	JOINED_PROGRAM = "joined_program:all",
	VENDOR_USERS_ALL = "vendor_users:all",
	VENDORS_ALL = "vendors:all",
	VENDOR_TEMPLATES_COUNT = "templates:count",
	VENDOR_CLIENT_COUNT = "client:count",
	VENDOR_CLIENT_COMPANIES_NAME = "vendor_client_companies:name",
	VENDOR_CLIENT_COMPANIES_ALL = "vendor_client_companies:all",
	PARTNER_MANAGER_ALL = "partner_manager:all",
	PARTNER_TIERS_ALL = "partner_tiers:all",
	PARTNER_TIERS_COUNT = "partners:count",
	VENDOR_OPPORTUNITIES_INSIGHTS = "vendor_opportunities:insights",
	PARTNER_OPPORTUNITIES_INSIGHTS = "partner_opportunities:insights",
	COMPANY_JOIN_INTENT = "company_join_intent",
	SHARED_CLIENTS_ALL = "shared_clients:all",
	OPPORTUNITIES_IS_DUPLICATE = "opportunities:is_duplicate",
	VENDOR_TRANSACTIONS_ALL = "transactions:all",
	VENDOR_INVOICES_ALL = "pa_invoices:all",
	COMMISSION_RECORDS_ALL = "commission_records:all",
	COMMISSIONS_ALL = "commissions:all",
	COMPANY_FULL = "company:full",
	CURRENT_USER = "users:current_user"
}

export const includeChildEntities = (
	childEntities: ChildEntityHydrationParams[] | null | undefined
) =>
	childEntities ? `included_child_entities=${childEntities.join(",")}` : "";

export const availableFonts: string[] = [
	"Source Sans Pro=Source Sans Pro, sans-serif;",
	"Andale Mono=andale mono, times, sans-serif;",
	"Arial=arial, sans-serif;",
	"Arial Black=arial black, avant garde, sans-serif;",
	"Comic Sans MS=comic sans ms, sans-serif;",
	"Courier New=courier new, courier, sans-serif;",
	"Georgia=georgia, palatino, sans-serif;",
	"Helvetica=helvetica, sans-serif;",
	"Impact=impact, sans-serif;",
	"Inter=Inter, sans-serif;",
	"Raleway=Raleway, sans-serif;",
	"Satoshi=Satoshi, sans-serif;",
	"Symbol=symbol, sans-serif;",
	"Tahoma=Tahoma, sans-serif;",
	"Terminal=terminal, monaco, sans-serif;",
	"Times New Roman=times new roman, times, sans-serif;",
	"Trebuchet=trebuchet, geneva, sans-serif;",
	"Titillium Web=Titillium Web, sans-serif;",
	"Verdana=verdana, geneva, sans-serif;"
];

export const availableFontSize: string[] = [
	"8px",
	"9px",
	"10px",
	"11px",
	"12px",
	"13px",
	"14px",
	"15px",
	"16px",
	"18px",
	"20px",
	"24px",
	"30px",
	"32px",
	"36px",
	"48px",
	"60px",
	"72px",
	"96px"
];

export const availableFormatBlocks: string[] = [
	"Paragraph=p;",
	"Header 1=h1;",
	"Header 2=h2;",
	"Header 3=h3;",
	"Header 4=h4;",
	"Header 5=h5;",
	"Header 6=h6"
];

export const textEditorFonts = availableFonts.map(f => f.split("=")[0]);

export const textEditorFontFormats = [
	{ label: "Paragraph", value: "p" },
	{ label: "Header 1", value: "h1" },
	{ label: "Header 2", value: "h2" },
	{ label: "Header 3", value: "h3" },
	{ label: "Header 4", value: "h4" },
	{ label: "Header 5", value: "h5" },
	{ label: "Header 6", value: "h6" }
];

export const lineHeightFormats: string[] = [
	"1",
	"1.1",
	"1.2",
	"1.3",
	"1.4",
	"1.5",
	"2"
];

export const TinyMceTextBlockPlugins =
	"autolink table advlist lists anchor link";

export const TinyMcePlugins = TinyMceTextBlockPlugins + " image autoresize";

export const TinyMceChildTextBlockPlugins = TinyMceTextBlockPlugins + " image";

export const TinyMceValidHtmlElementAttributes =
	"h1[align],h2[align],h3[align],h4[align],h5[align],h6[align],a[href|target=_blank|rel|name|download],strong[align],b[align],i[align],u[align],em[align],small[align],big[align],pre[align],code[align],cite[align],blockquote[align],sup[align],sub[align],strike[align],center[align],hr[align],ul[align],li[align],ol[align],dd[align],dt[align],dl[align],fieldset[align],span[align],p[align],label[for],div[align],br,img[src|border=0|alt|title|name|hspace|vspace|width|height|align],video[src|border=0|alt|title|name|hspace|vspace|width|height|align],table[border|bgcolor|noresize|background|align|caption|cellpadding|cellspacing|height|width|halign|valign|char|charoff],caption[align|bgcolor|background|abbr|axis|headers|scope|nowrap|height|width],tbody[align|bgcolor|background|abbr|axis|headers|scope|nowrap|height|width|halign|valign|char|charoff],theader[align|bgcolor|background|abbr|axis|headers|scope|nowrap|height|width|halign|valign|char|charoff],tfooter[align|bgcolor|background|abbr|axis|headers|scope|nowrap|height|width|halign|valign|char|charoff],tr[colspan|rowspan|align|bgcolor|background|abbr|axis|headers|scope|nowrap|height|width|halign|valign|char|charoff],td[colspan|rowspan|align|bgcolor|background|abbr|axis|headers|scope|nowrap|height|width|halign|valign|char|charoff],th[colspan|rowspan|align|bgcolor|background|abbr|axis|headers|scope|nowrap|height|width|halign|valign|char|charoff],col[span|width|align|bgcolor|background|abbr|axis|headers|scope|nowrap|height|width|halign|valign|char|charoff],colgroup[span|width|align|bgcolor|background|abbr|axis|headers|scope|nowrap|height|width|halign|valign|char|charoff],fig[align|bgcolor|background|abbr|axis|headers|scope|nowrap|height|width]";

export const TinyMceOptions = {
	license_key: "gpl",
	branding: false,
	text_patterns: [
		{
			start: "*",
			end: "*",
			format: "italic"
		},
		{
			start: "_",
			end: "_",
			format: "italic"
		},
		{
			start: "**",
			end: "**",
			format: "bold"
		},
		{
			start: "#",
			format: "h1"
		},
		{
			start: "##",
			format: "h2"
		},
		{
			start: "###",
			format: "h3"
		},
		{
			start: "####",
			format: "h4"
		},
		{
			start: "#####",
			format: "h5"
		},
		{
			start: "######",
			format: "h6"
		},
		{
			start: "1. ",
			cmd: "InsertOrderedList"
		},
		{
			start: "* ",
			cmd: "InsertUnorderedList"
		},
		{
			start: "- ",
			cmd: "InsertUnorderedList"
		}
	],
	menubar: false,
	browser_spellcheck: true,
	contextmenu: false as const,
	statusbar: false,
	elementpath: false,
	default_link_target: "_blank",
	link_context_toolbar: true,
	link_title: false,
	plugins: TinyMcePlugins,
	toolbar: [
		`blocks fontfamily fontsize | bold italic underline | strikethrough forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | link image | table`
	],
	mobile: {
		theme: "silver"
	},
	content_css: [
		"https://fonts.googleapis.com/css?family=Source+Sans+Pro:400,600",
		"https://fonts.googleapis.com/css2?family=Titillium+Web:wght@400;600&display=swap",
		"https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,300;0,400;1,300;1,400&display=swap",
		"https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap",
		"https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700&display=swap"
	],
	block_formats: availableFormatBlocks.join(" "),
	font_family_formats: availableFonts.join(" "),
	font_size_formats: availableFontSize.join(" "),
	paste_data_images: true,
	paste_as_text: false,
	paste_webkit_styles: "none",
	paste_merge_formats: true,
	paste_remove_styles_if_webkit: true,
	table_grid: true,
	table_tab_navigation: true,
	table_default_attributes: {
		border: "1",
		caption: false,
		align: "left"
	},
	table_default_styles: {
		"border-collapse": "collapse",
		width: "100%"
	},
	table_resize_bars: true,
	table_appearance_options: true,
	table_advtab: true,
	table_row_advtab: true,
	images_upload_url: `${process.env.REACT_APP_API_HOST}/v2/files/upload`,
	automatic_uploads: true,
	file_picker_types: "file image media",
	images_reuse_filename: true,
	images_upload_credentials: true,
	images_upload_handler: async (blobInfo: any, progress: any) => {
		return new Promise((resolve, reject) => {
			const authToken = cache.get(
				ZomentumStorageKeys.Token,
				true
			) as AuthToken;
			const formData = new FormData();
			formData.append("file", blobInfo.blob(), blobInfo.filename());
			const url = `${process.env.REACT_APP_API_HOST}/v2/files/upload`;
			const options: AxiosRequestConfig & { curlirize: boolean } = {
				method: "post",
				curlirize: false,
				data: formData,
				headers: {
					"Content-Type": "multipart/form-data",
					Authorization: "Bearer " + authToken.token
				},
				onUploadProgress: (event: any) => {
					progress((event.loaded / event?.total) * 100);
				}
			};
			axios
				.post(url, formData, options)
				.then(response => {
					if (response.status === 403) {
						reject({
							message: "Error: " + response.status,
							remove: true
						});
						return;
					}
					if (response.status < 200 || response.status >= 300) {
						reject("Error: " + response.status);
						return;
					}

					const json = response.data;

					if (!json.upload_url) {
						reject("Invalid: " + JSON.stringify(json));
						return;
					}
					resolve(json.upload_url);
				})
				.catch(error => {
					reject("Image upload failed due to code: " + error.message);
					console.error(error);
				});
		});
	},
	media_alt_source: true,
	media_dimensions: true,
	media_filter_html: true,
	media_live_embeds: true,
	setup: (editor: any) => {
		editor.on("BeforeExecCommand", (e: any) => {
			const textFormatCommands = [
				"Bold",
				"Italic",
				"Underline",
				"mceToggleFormat",
				"FontName",
				"FontSize"
			];
			if (textFormatCommands.indexOf(e.command) !== -1) {
				const nonEditableElements = editor
					.getBody()
					.querySelectorAll(".mceNonEditable");
				nonEditableElements.forEach((element: any) => {
					element.removeAttribute("contenteditable");
					element.removeAttribute("data-mce-contenteditable");
				});
			}

			if (
				e.command === "FontSize" ||
				e.command === "FontName" ||
				e.command === "ForeColor"
			) {
				const val = e.value;
				const node = e.target.selection.getNode();
				const nodeParent = node.parentNode;

				const nodeParentText = nodeParent.innerText;
				const nodeText = node.innerText;

				if (
					node.nodeName === "SPAN" &&
					nodeParent.nodeName === "LI" &&
					nodeParentText === nodeText
				) {
					if (e.command === "FontSize") {
						editor.dom.setStyle(nodeParent, "font-size", val);
					}
					if (e.command === "FontName") {
						editor.dom.setStyle(nodeParent, "font-family", val);
					}
					if (e.command === "ForeColor") {
						editor.dom.setStyle(nodeParent, "color", val);
					}
				} else if (node.nodeName === "UL" || node.nodeName === "OL") {
					const li = editor.dom.select("li", node);
					if (e.command === "FontSize") {
						editor.dom.setStyle(li, "font-size", val);
					}
					if (e.command === "FontName") {
						editor.dom.setStyle(li, "font-family", val);
					}
					if (e.command === "ForeColor") {
						editor.dom.setStyle(li, "color", val);
					}
				}
			}
		});
		editor.on("ExecCommand", () => {
			const nonEditableElements = editor
				.getBody()
				.querySelectorAll(".mceNonEditable");
			nonEditableElements.forEach((element: any) => {
				element.setAttribute("contenteditable", "false");
				element.setAttribute("data-mce-contenteditable", "false");
			});
		});

		editor.ui.registry.addButton("attachment", {
			icon: "upload",
			tooltip: "Insert attachment",
			onAction: () => {
				const fileUploader: HTMLElement | null =
					document.querySelector("#file");
				if (fileUploader) {
					fileUploader.click();
				}
			}
		});
	},
	valid_elements:
		window.isDevelopment || window.isStaging
			? TinyMceValidHtmlElementAttributes
			: undefined,
	convert_unsafe_embeds: true,
	sandbox_iframes: true
};

export enum TinyMcePasteOption {
	PASTE_WITHOUT_FORMATTING,
	PASTE_WITH_FORMATTING
}

export const htmlToPlainText = (content: string) => {
	return htmlToText(content, {
		wordwrap: false,
		ignoreHref: true,
		ignoreImage: true
	})
		.replace(/(?:\r\n|\r|\n)/g, "<br>")
		.replace(/<br><br>/g, "<br>");
};

export const copyToClipboardFromInputElement = (textToCopy: string) => {
	try {
		const element = document.createElement("textarea");
		element.value = textToCopy;
		element.style.position = "absolute";
		element.style.left = "-9999";
		element.style.top = "-9999";
		document.body.appendChild(element);
		element.select();
		document.execCommand("copy");
		document.body.removeChild(element);
	} catch (error) {
		console.error(error);
	}
};

export const isDocumentCustomerViewUrl = (pathname: string): boolean => {
	if (!pathname) {
		return false;
	}
	const documentReadonlyViewUrlRegex =
		/\/(documents\/view\/)([0-9a-f]{8}[0-9a-f]{4}[1-5][0-9a-f]{3}[89ab][0-9a-f]{3}[0-9a-f]{12})/;
	return pathname.match(documentReadonlyViewUrlRegex) ? true : false;
};

export const isDocumentPdfViewUrl = (pathname: string): boolean => {
	if (!pathname) {
		return false;
	}
	const documentPdfViewUrlRegex =
		/\/(documents\/view\/pdf\/)([0-9a-f]{8}[0-9a-f]{4}[1-5][0-9a-f]{3}[89ab][0-9a-f]{3}[0-9a-f]{12})/;
	return pathname.match(documentPdfViewUrlRegex) ? true : false;
};

export const isVerifyPaymentViewUrl = (pathname: string): boolean => {
	if (!pathname) return false;
	const documentPdfViewUrlRegex =
		/\/(payment\/)([0-9a-f]{8}[0-9a-f]{4}[1-5][0-9a-f]{3}[89ab][0-9a-f]{3}[0-9a-f]{12})/;
	return pathname.match(documentPdfViewUrlRegex) ? true : false;
};

export const isDocumentClientView = (pathname: string): boolean => {
	if (!pathname) {
		return false;
	}

	const isDocumentURL = isDocumentCustomerViewUrl(pathname);
	const queryAuthParams = getQueryParamAuthDetails();

	return !!(isDocumentURL && queryAuthParams);
};

export const getMergeTagsFromText = (textContent: string) => {
	// using .replace to remove '{{' and '}}' because regex group doesn't work in firefox

	const mergeTags = textContent
		.match(/\{\{[^{}<>]+\}\}/gm)
		?.map(tag => tag.replace("{{", "").replace("}}", ""));

	return mergeTags;
};

export const checkForDocumentUrlPresent = (textContent: string) => {
	const isDocUrlPresent = textContent.match(/{{Receiver[.]Document[.]Url}}/gm)
		? true
		: false;
	return isDocUrlPresent;
};

// this returns a boolean which check whether to hide the sidebar and navbar or not
export const shouldHideLayoutForPath = (pathname: string): boolean =>
	isDocumentCustomerViewUrl(pathname) ||
	isDocumentPdfViewUrl(pathname) ||
	pathname === ZomentumRoutes.DocumentSuccessSignature ||
	pathname === ZomentumRoutes.Ms365AssessmentSuccess ||
	pathname === ZomentumRoutes.LoginOrSignup ||
	pathname === ZomentumRoutes.Login ||
	pathname === ZomentumRoutes.VerifyEmail ||
	pathname === ZomentumRoutes.VerifyPayment ||
	pathname === ZomentumRoutes.Authorize;

// getting the user's getStream Config from global state
export const getUserGetStreamConfig = (
	configurationSettings: ConfigurationSettingsResponse
): NotificationConfiguration =>
	configurationSettings &&
	configurationSettings.user_configs &&
	configurationSettings.user_configs.notification;

interface OptionData {
	key?: string | number;
	disabled?: boolean;
	value: string | number;
	title?: string;
	children?: string;
	className?: string;
	label?: React.ReactNode;
	options?: OptionData[];
}

export const filterOptionsOnSearch = (
	inputValue: string,
	option?: any
): boolean => {
	if (inputValue.length < 3) return false;
	return filterSelectByTextInput(inputValue, option);
};

export const filterSelectByTextInput = (
	inputValue: string,
	option: any
): boolean => {
	const currOption = option as OptionData;
	const optionValue =
		typeof currOption.children === "string"
			? currOption.children
			: /*
				If an option is not plain text, but a React component like ZText. Check with React's isValidElement function.
				Example:
				option: {
					name: <ZText>XYZ</ZText>
					value: 123
				}

				We target the children from props, and limitation is it should be a plain string.
				Else, it would be recursive loop until we find a string, which we can avoid by not nesting components.
			*/
			isValidElement(currOption.children)
			? (currOption.children as ReactElement).props?.children
			: currOption.value;

	// If it's an opt group, return false.
	// https://github.com/ant-design/ant-design/issues/27956#issuecomment-990014448
	const isGroup = Array.isArray(currOption.options);
	if (isGroup) {
		return false;
	}

	return !!(
		optionValue
			?.toString()
			?.toLowerCase()
			?.indexOf(inputValue.toLowerCase()) >= 0
	);
};

export enum QueryParamConstants {
	MSP_NAME = "msp_name",
	MS365_MODAL_OPEN = "ms365_modal_open",
	USER_ID = "user_id",
	CLIENT_USER_ID = "client_user_id",
	SETTINGS_PANEL = "settings_panel"
}

export class QueryParamCredentials {
	queryParam = "";

	constructor(userType: UserType, id: string) {
		switch (userType) {
			case UserType.User:
				this.queryParam = `${QueryParamConstants.USER_ID}=${id}`;
				break;
			case UserType.ClientUser:
				this.queryParam = `${QueryParamConstants.CLIENT_USER_ID}=${id}`;
				break;
			default:
				this.queryParam = "";
				break;
		}
	}
}

export const getQueryParamAuthDetails = (): QueryParamCredentials | null => {
	const userAuth = getQueryVariable(QueryParamConstants.USER_ID);
	if (userAuth) {
		return new QueryParamCredentials(UserType.User, userAuth);
	}

	const clientAuth = getQueryVariable(QueryParamConstants.CLIENT_USER_ID);
	if (clientAuth) {
		return new QueryParamCredentials(UserType.ClientUser, clientAuth);
	}

	return null;
};

export const isValidTextString = (text: string): boolean => {
	//will accept any emoji as first character in comment but will ignore only spaces or newline without any valid text.
	const wordRegex =
		/(\w|\u00a9|\u00ae|[\u0021-\u007e]|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;
	return text ? !isNull(text.match(wordRegex)) : false;
};

export const getQueryVariable = (variable: string): string | null => {
	const query = window.location.search?.substring(1);
	const vars = query?.split("&");
	if (!vars) {
		return null;
	}

	for (const currVar of vars) {
		const pair = currVar.split("=");
		if (decodeURIComponent(pair[0]) === variable) {
			return decodeURIComponent(pair[1]);
		}
	}
	return null;
};

export const generateColorStringFromColor = (color: RGBColor): string => {
	return `rgba(${!isNaN(Number(color.r)) ? color.r : 0}, ${
		!isNaN(Number(color.g)) ? color.g : 0
	}, ${!isNaN(Number(color.b)) ? color.b : 0}, ${
		!isNaN(Number(color.a))
			? Number(color.a) > 1
				? Number(color.a) / 100
				: color.a
			: 1
	})`;
};

export const removeAlphaFromRgba = (color: string) => {
	const match = color.match(
		/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/
	);
	if (!match) {
		return color;
	}
	const [, r, g, b] = match;
	return `rgb(${r}, ${g}, ${b})`;
};

export const generateColorObjFromString = (str: string): RGBColor => {
	const [r, g, b, a] = str
		.replace(/\s/g, "")
		.substring(str.indexOf("(") + 1, str.lastIndexOf(")") - 1)
		.replace(/\(/g, "")
		.replace(/\)/g, "")
		.split(",")
		.map(e => Number(e));
	return { r, g, b, a };
};

export type WithQueryFilter<T> = T & {
	included_child_entities: ChildEntityHydrationParams[];
};

export const defaultColorValue: RGBColor = {
	r: 0,
	g: 0,
	b: 0,
	a: 1
};

export const defaultBackgroundColorValue: RGBColor = {
	r: 255,
	g: 255,
	b: 255,
	a: 1
};

export const defaultQuoteBlockBackgroundColorValue: RGBColor = {
	r: 47,
	g: 112,
	b: 255,
	a: 1
};
export const tryUntilSuccess = (
	functionToTry: () => any,
	callback?: () => any,
	retry = 10
) => {
	try {
		functionToTry();
		callback && callback();
	} catch (error) {
		if (retry > 0) {
			setTimeout(
				() => tryUntilSuccess(functionToTry, callback, retry - 1),
				500 + (10 - retry) * 500
			);
		} else {
			console.error(
				"Tried multiple times but couldnt successfully make it happen!",
				error
			);
		}
	}
};

export const currentLocationIs = (route: ZomentumRoutes): boolean => {
	const idRemoved = route.replace(":id", "");
	// Do NOT remove the `?` conditional checks here because window/document related properties will not be available in CI/test/SSR environments
	if (window?.location?.pathname?.startsWith(idRemoved)) {
		return true;
	} else {
		return false;
	}
};

export function getInitialsForAvatar(name: string | undefined): string {
	return (
		(name?.length &&
			name
				?.split(" ")
				.map(word => word[0] + "")
				.filter(a => !a.match(/[^\w\s]/gi))
				.join("")
				.substring(0, 3)
				.toUpperCase()) ||
		"--"
	);
}

export const toggleBodyTextSelectableClass = (visible: boolean) => {
	if (visible) {
		// Do NOT remove the `?` conditional checks here because window/document related properties will not be available in CI/test/SSR environments
		document?.body?.classList?.add("disable-text-selection");
	} else {
		document?.body?.classList?.remove("disable-text-selection");
	}
};

export const validateForm = <T>(form: WrappedFormUtils<T>): Promise<T> => {
	return new Promise((resolve, reject) => {
		form.validateFieldsAndScroll(
			{ scroll: { offsetBottom: 150 } },
			(err, values) => {
				if (!err) {
					try {
						resolve(values);
					} catch (error) {
						console.error(error);
					}
				} else {
					reject(err);
				}
			}
		);
	});
};

export const formatDate = (value: string): string => {
	return !isNaN(Date.parse(value))
		? moment(value).format(DefaultTimestampFormat)
		: value;
};

export const validUrlRegex =
	/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;

export const validFQDNRegex =
	/^(?!:\/\/)(?=.{1,253}$)(?:[a-zA-Z0-9](?:[a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}\.?$/i;

export const validIPV4Regex =
	/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
export const validIPV6Regex =
	/(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/;
export const emailRegex =
	/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,15}/;
export const phoneRegex = /^[\d()+-]{0,20}$/;
export const whiteSpaceBeforeForwardSlash = /\s\/$/;

export const stripProtocol = (url: string): string =>
	url
		.replace(/^\w+:\/\//, "")
		.split("/")[0]
		.replace(/:[0-9]+$/, "");

export const formPhoneRules = [
	{
		pattern: phoneRegex,
		message:
			"Only numbers from 0-9 and special characters +, -, (, ) are allowed."
	}
];

export interface ZomentumFieldMapping {
	entity: string;
	field: string;
	mapping: Map<string, string>;
}

const YOUTUBE_LINK_REGEX =
	/^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w\\-]+\?v=|embed\/|v\/)?)([\w\\-]+)(\S+)?$/g;

export const validateYoutubeLink = (val: string): RegExpMatchArray | null =>
	val.match(YOUTUBE_LINK_REGEX);

export const getYoutubeID = (youtubeURL: string): string | null => {
	const youtubeIdRegEx = new RegExp(
		/(?:\/|%3D|v=|vi=)([0-9A-z-_]{11})(?:[%#?&]|$)/gm
	);
	return youtubeIdRegEx.exec(youtubeURL)?.[1] ?? null;
};

export const checkValidUrl = (url: string): boolean => {
	const res = url.match(validUrlRegex);
	return !!res;
};

export const isArrayEqual = (x: PricingAddOn[], y: PricingAddOn[]) => {
	return isEmpty(xorWith(x, y, isEqual));
};

export const calculateSelectedPlanPrice = (planTier?: PricingPlanTiers) => {
	let price = 0;
	planTier?.tier.forEach(eachTier => {
		if (
			eachTier.starting_unit < 2 &&
			(eachTier.ending_unit !== 2 || eachTier.ending_unit === 2)
		) {
			price += eachTier.price;
		}
	});
	return price;
};

export const hiddenEmail = (value: string) => {
	const emailId = value.split("@")[0];
	const domain = value.split("@")[1].split(".")[0];

	const finalString =
		emailId.slice(0, 2) +
		Array(emailId.length - 2)
			.fill("*")
			.join("");

	const hiddenDomain =
		domain.slice(0, 1) +
		Array(domain.length - 2)
			.fill("*")
			.join("") +
		domain.slice(-1);

	return (
		finalString +
		"@" +
		hiddenDomain +
		"." +
		value.split("@")[1].split(".").slice(1).join(".")
	);
};
export const getProductCatalogResultDisplayPrice = (
	pricing: { cost_price: number | null; sell_price: number | null } | null,
	currentItemSource?: ProductSource
) =>
	currentItemSource === ProductSource.Self ||
	currentItemSource === ProductSource.ZomentumMarketplace
		? pricing?.sell_price ?? 0
		: pricing?.cost_price ?? 0;

export const getErrorMessageFromJSON = (
	error: any
): KaseyaErrorObject | undefined => {
	const objectMatcher = /\{(?:[^{}]|)*\}/g;
	return JSON.parse(objectMatcher.exec(error.message)?.[0] ?? "");
};

export interface Dictionary<T> {
	[index: string]: T;
}

export interface DebouncedFunc<T extends (...args: any[]) => any> {
	/**
	 * Call the original function, but applying the debounce rules.
	 *
	 * If the debounced function can be run immediately, this calls it and returns its return
	 * value.
	 *
	 * Otherwise, it returns the return value of the last invocation, or undefined if the debounced
	 * function was not invoked yet.
	 */
	(...args: Parameters<T>): ReturnType<T> | undefined;

	/**
	 * Throw away any pending invocation of the debounced function.
	 */
	cancel(): void;

	/**
	 * If there is a pending invocation of the debounced function, invoke it immediately and return
	 * its return value.
	 *
	 * Otherwise, return the value from the last invocation, or undefined if the debounced function
	 * was never invoked.
	 */
	flush(): ReturnType<T> | undefined;
}

export const fetchHostRedirectionUrl = (
	authToken: AuthToken,
	userConfig: ConfigurationSettingsResponse
): string | null => {
	if (
		authToken &&
		!!userConfig?.domain?.length &&
		!!authToken?.token?.length &&
		window.location.hostname !== "localhost" &&
		window.location.hostname !== process.env.REACT_APP_DOMAIN && // This statement is used while "npm run buildReviewAppStaging --domain=subdomain.domain.com"
		window.location.origin.toLowerCase() !== userConfig.domain.toLowerCase()
	) {
		return `${userConfig.domain.toLowerCase()}/authorize`;
	}
	return null;
};

export const shippingAddressFieldNullCheck = (
	shippingAddress: PurchaseOrderAddress | null
): keyof PurchaseOrderAddress => {
	let nullKey;
	const avoidKeys = [
		"id",
		"address_line_2",
		"synced_data",
		"is_default",
		"phone"
	];
	if (shippingAddress) {
		nullKey = Object.keys(shippingAddress)
			.filter(key => !avoidKeys.includes(key))
			?.find(
				(key: string) =>
					!shippingAddress[key as keyof PurchaseOrderAddress]
			);
		return (nullKey as keyof PurchaseOrderAddress) || null;
	} else {
		nullKey = Object.keys(new PurchaseOrderAddress())[0];
	}
	return nullKey as keyof PurchaseOrderAddress;
};
export const getMimeType = (mimeType: string | null): ResourceFileType => {
	if (mimeType == null || mimeType.length === 0)
		return ResourceFileType.Folder;
	else if (mimeType.startsWith("image/")) {
		if (
			mimeType.startsWith("image/svg") ||
			mimeType.startsWith("image/svg+xml") ||
			mimeType.startsWith("image/webp")
		) {
			return ResourceFileType.SVG;
		}
		return ResourceFileType.Image;
	} else if (
		mimeType.startsWith("video/") ||
		mimeType.startsWith("application/octet-stream")
	) {
		return ResourceFileType.Video;
	} else if (mimeType.startsWith("application/pdf")) {
		return ResourceFileType.Pdf;
	} else if (mimeType.startsWith("text/plain")) {
		return ResourceFileType.Txt;
	} else if (
		mimeType.indexOf("presentation") >= 0 ||
		mimeType.indexOf("vnd.ms-powerpoint") >= 0
	) {
		return ResourceFileType.Presentation;
	} else if (
		mimeType.indexOf("document") >= 0 ||
		mimeType.indexOf("application/msword") >= 0
	) {
		return ResourceFileType.Document;
	} else if (
		mimeType.indexOf("sheet") >= 0 ||
		mimeType.indexOf("vnd.ms-excel") >= 0
	) {
		return ResourceFileType.Spreadsheet;
	} else if (mimeType.indexOf("text/csv") >= 0) {
		return ResourceFileType.Csv;
	} else if (mimeType?.startsWith("text/html; charset=utf-8")) {
		return ResourceFileType.Youtube;
	}
	return ResourceFileType.Document;
};
export const getTwoToneIconFromMimeType = (
	mimeType: string | null
): EIcons | ETwoToneIcons => {
	const resourceFileType: ResourceFileType = getMimeType(mimeType);

	if (
		resourceFileType === ResourceFileType.Image ||
		resourceFileType === ResourceFileType.SVG
	)
		return ETwoToneIcons.RESOURCEIMAGETHUMBNAIL;
	else if (resourceFileType === ResourceFileType.Video)
		return ETwoToneIcons.RESOURCEVIDEOTHUMBNAIL;
	else if (resourceFileType === ResourceFileType.Pdf)
		return ETwoToneIcons.RESOURCEPDFTHUMBNAIL;
	else if (resourceFileType === ResourceFileType.Txt)
		return ETwoToneIcons.RESOURCETXTTHUMBNAIL;
	else if (resourceFileType === ResourceFileType.Presentation)
		return ETwoToneIcons.RESOURCEPRESENTATIONTHUMBNAIL;
	else if (resourceFileType === ResourceFileType.Document)
		return ETwoToneIcons.RESOURCEDOCTHUMBNAIL;
	else if (resourceFileType === ResourceFileType.Spreadsheet)
		return ETwoToneIcons.RESOURCESPREADSHEETTHUMBNAIL;
	else if (resourceFileType === ResourceFileType.Csv)
		return ETwoToneIcons.RESOURCECSVTHUMBNAIL;
	else return ETwoToneIcons.RESOURCEDOCTHUMBNAIL;
};

export const getIconFromMimeType = (mimeType: string | null): EIcons => {
	const resourceFileType: ResourceFileType = getMimeType(mimeType);
	if (resourceFileType === ResourceFileType.Folder) return EIcons.FOLDER;
	else if (
		resourceFileType === ResourceFileType.Image ||
		resourceFileType === ResourceFileType.SVG
	)
		return EIcons.RESOURCEIMAGE;
	else if (resourceFileType === ResourceFileType.Video)
		return EIcons.RESOURCEVIDEO;
	else if (resourceFileType === ResourceFileType.Pdf)
		return EIcons.RESOURCEPDF;
	else if (resourceFileType === ResourceFileType.Txt)
		return EIcons.RESOURCETXT;
	else if (resourceFileType === ResourceFileType.Presentation)
		return EIcons.RESOURCEPRESENTATION;
	else if (resourceFileType === ResourceFileType.Document)
		return EIcons.RESOURCEDOCUMENT;
	else if (resourceFileType === ResourceFileType.Spreadsheet)
		return EIcons.RESOURCESPREADSHEET;
	else if (resourceFileType === ResourceFileType.Csv)
		return EIcons.RESOURCECSV;
	else return EIcons.RESOURCEDOCUMENT;
};

export const getCustomFieldsDefaultTypeValue = (field: CustomField) => {
	const fieldType = field.field_type;
	if (CustomFieldType.DateField === fieldType) {
		return moment();
	} else if (CustomFieldType.Dropdown === fieldType) {
		return field.allowed_dropdown_values?.[0] ?? "";
	} else if (CustomFieldType.CurrencyField === fieldType) {
		const userCompany = cache.get(
			cacheConstant.userCompany,
			true
		) as UserCompany;
		return userCompany?.settings?.currency ?? Currency.ALL;
	} else if (fieldType === CustomFieldType.DropdownFieldWithKeyValue) {
		return field.allowed_dropdown_key_values?.[0].key ?? "";
	}

	const valueMap: { [key: string]: string } = {
		[CustomFieldType.DecimalField]: "0.0",
		[CustomFieldType.AmountField]: "0.0",
		[CustomFieldType.NumberField]: "0",
		[CustomFieldType.TextField]: "sample data"
	};
	return valueMap[fieldType] ?? "";
};

export const fallbackImageURL =
	"data:image/png;base64,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";

export const getGridStylesForQuoteBlock = (
	hide_unit_price: boolean,
	hide_discount: boolean,
	hide_quantity: boolean,
	hide_amount: boolean
) => {
	return {
		gridTemplateAreas: `'details ${!hide_unit_price ? "price" : "."} ${
			!hide_quantity ? "quantity" : "."
		} ${!hide_discount ? "discount" : "."} ${!hide_amount ? "final" : ""}'`,
		gridTemplateColumns: `1fr ${!hide_unit_price ? "110px" : "auto"} ${
			!hide_quantity ? "62px" : "auto"
		} ${!hide_discount ? "92px" : "auto"} ${
			!hide_amount ? "110px" : "auto"
		}`
	};
};

export const getGridStylesForQuoteBlockWithCheckbox = (
	hide_unit_price: boolean,
	hide_discount: boolean,
	hide_quantity: boolean,
	hide_amount: boolean
) => {
	return {
		gridTemplateAreas: `'checkbox details ${
			!hide_unit_price ? "price" : "."
		} ${!hide_quantity ? "quantity" : "."} ${
			!hide_discount ? "discount" : "."
		} ${!hide_amount ? "final" : ""}'`,
		gridTemplateColumns: `20px 1fr ${!hide_unit_price ? "110px" : "auto"} ${
			!hide_quantity ? "62px" : "auto"
		} ${!hide_discount ? "92px" : "auto"} ${
			!hide_amount ? "110px" : "auto"
		}`
	};
};

export const passwordValidator = (_: any, value: string) => {
	if (!value?.length) return Promise.resolve();
	const hasMinLength = value.length >= 8;
	const hasUpperCase = /[A-Z]/.test(value);
	const hasLowerCase = /[a-z]/.test(value);
	const hasNumber = /\d/.test(value);
	const hasSpecialChar = /[^A-Za-z0-9]/.test(value);
	const hasMinCount =
		[hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar].filter(Boolean)
			.length >= 3;
	if (hasMinLength && hasMinCount) {
		return Promise.resolve();
	}
	return Promise.reject(
		new Error(
			"Allowed - min 8 characters, uppercase letters, lowercase letters, numbers and special characters"
		)
	);
};

export const getTimePeriod = (date: Date) => {
	return moment(date).isAfter(moment().subtract(24, "hours"))
		? moment(date).fromNow()
		: moment(date).format("LLL");
};
